import {
  PITCH_OPTIONS,
  WALL_MEASUREMENT_FIELDS,
  WASTE_PCT_OPTIONS,
  isHoverReport,
  parseHoverWalls,
  parseRoofReport,
  reportNameMismatch,
  type Job,
  type RoofLayout,
  type WallMeasurement,
} from "@roof-hawk/core";
import { useRef, useState } from "react";

import { Modal } from "../../components/ui";
import { t, tn } from "../../i18n";
import type { RoofLayoutInput } from "./RoofMeasurementModal";
import { RoofOffer, roofWithoutPitch } from "./RoofOffer";

type Draft = Record<string, string>;

const emptyDraft = (): Draft =>
  Object.fromEntries(WALL_MEASUREMENT_FIELDS.map((f) => [f.key, ""]));

const num = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export interface WallMeasurementInput {
  name: string | null;
  waste_pct: number;
  stories: number;
  source: string;
  pdf_filename: string | null;
  pdfFile: File | null;
  values: Record<string, number | null>;
  /**
   * The roof off the same report, when the rep left the box ticked. A Hover
   * report measures the whole house, and Jonathon, 2026-09-30, asked that a
   * report dropped on a siding job fill the roof card too: "yes".
   */
  roof: RoofLayoutInput | null;
}

/**
 * Wall Measurement.
 *
 * The siding's numbers, kept apart from the roof's. Two ways in, the same as
 * the roof: drop the Hover report on it and the boxes fill for the rep to
 * confirm, or key them by hand. See packages/core/src/siding.ts for what each
 * box drives.
 */
export function WallMeasurementModal({
  job,
  walls,
  layouts,
  onClose,
  onSave,
  onDelete,
}: {
  job: Job;
  walls: readonly WallMeasurement[];
  /**
   * The roofs already measured on the job. Every roof template adds up every
   * layout on the job, so the roof off a Hover report is offered unticked
   * when one is here — saving it again would count the roof twice.
   */
  layouts: readonly RoofLayout[];
  onClose: () => void;
  onSave: (input: WallMeasurementInput) => Promise<void>;
  onDelete: (wall: WallMeasurement) => Promise<void>;
}) {
  const [mode, setMode] = useState<"choose" | "manual">(
    walls.length ? "choose" : "manual",
  );

  if (mode === "choose") {
    return (
      <Modal
        title={t("Wall Measurement")}
        subtitle={t("Every set of walls measured on this job.")}
        onClose={onClose}
        actions={
          <>
            <button className="btn-ghost" onClick={onClose}>
              {t("Close")}
            </button>
            <button className="btn-primary" onClick={() => setMode("manual")}>
              {t("Add walls")}
            </button>
          </>
        }
      >
        {walls.map((wall) => (
          <div className="layrow" key={wall.id}>
            <div className="laymain">
              <div className="an">{wall.name || t("Untitled walls")}</div>
              <div className="na-hint">
                {t("{sqft} sq ft siding · {waste}% waste", {
                  sqft: wall.siding_trim_sqft ?? wall.siding_sqft ?? "—",
                  waste: wall.waste_pct ?? 0,
                })}{" "}
                · {tn(Number(wall.stories ?? 1), "1 story", "{n} stories")}
                {wall.pdf_filename ? ` · ${wall.pdf_filename}` : ""}
              </div>
            </div>
            <button
              type="button"
              className="btn-small danger"
              onClick={() => void onDelete(wall)}
            >
              {t("Remove")}
            </button>
          </div>
        ))}
      </Modal>
    );
  }

  return (
    <WallForm
      job={job}
      layouts={layouts}
      onClose={walls.length ? () => setMode("choose") : onClose}
      onSave={onSave}
    />
  );
}

function WallForm({
  job,
  layouts,
  onClose,
  onSave,
}: {
  job: Job;
  layouts: readonly RoofLayout[];
  onClose: () => void;
  onSave: (input: WallMeasurementInput) => Promise<void>;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [waste, setWaste] = useState("");
  const [stories, setStories] = useState("1");
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [filled, setFilled] = useState<string[]>([]);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [nameWarning, setNameWarning] = useState<{
    reported: string;
    customer: string;
  } | null>(null);
  /*
   * The roof off the same report. Hover measures the whole house, so the
   * roof card can be filled from the same drop. Held here, shown as one
   * ticked box with the pitch and a waste pick of its own, because Hover
   * recommends no roof waste and the wall's factor is not the roof's. The box
   * starts ticked only on a job with no roof measured yet: the roof templates
   * add every layout up, so a second one doubles the squares.
   */
  const [roof, setRoof] = useState<{
    values: Record<string, number>;
    pitch: string;
    count: number;
  } | null>(null);
  const [saveRoof, setSaveRoof] = useState(layouts.length === 0);
  const [roofWaste, setRoofWaste] = useState("10");
  const [busy, setBusy] = useState(false);

  // Submit waits for a ticked roof's pitch (#623; see `roofWithoutPitch`).
  const ready =
    waste !== "" && !roofWithoutPitch(roof, saveRoof) && !busy && !reading;

  async function readPdf(file: File) {
    setReading(true);
    setReadError(null);
    setNameWarning(null);
    setRoof(null);
    try {
      // Loaded on demand: pdf.js is too big to ride in the main bundle.
      const { extractPdfReport } = await import("../../lib/roofPdf");
      const report = await extractPdfReport(file);
      const hover = isHoverReport(report.text);
      const parsed = parseHoverWalls(report.text);
      setNameWarning(
        reportNameMismatch(report.text, job.contact?.full_name ?? null),
      );
      setPdfFile(file);
      setName((current) => current || file.name.replace(/\.pdf$/i, ""));
      if (parsed.stories !== null) setStories(String(parsed.stories));
      setDraft((current) => {
        const next = { ...current };
        for (const [key, value] of Object.entries(parsed.values)) {
          if (key in next) next[key] = String(value);
        }
        return next;
      });
      setFilled(parsed.matched);
      if (!hover) {
        setReadError(
          t(
            "That does not look like a Hover report. Wall measurements are read from Hover's Complete Measurements PDF — the file is attached; enter the numbers below if it is something else.",
          ),
        );
      } else if (parsed.matched.length === 0) {
        setReadError(
          t(
            "No wall measurements found in that PDF. The report is attached — enter the numbers below.",
          ),
        );
      }

      // And the roof, read by the same reader the roof card uses.
      const roofParsed = parseRoofReport(report.text, report.rows);
      if (roofParsed.matched.length > 0) {
        setRoof({
          values: roofParsed.values,
          pitch:
            roofParsed.averagePitch &&
            PITCH_OPTIONS.includes(roofParsed.averagePitch)
              ? roofParsed.averagePitch
              : "",
          count: roofParsed.matched.length,
        });
      }
    } catch (e) {
      setReadError(
        e instanceof Error
          ? t("Could not read that PDF — {message}", { message: e.message })
          : String(e),
      );
    } finally {
      setReading(false);
    }
  }

  async function submit() {
    if (!ready) return;
    setBusy(true);
    try {
      await onSave({
        name: name.trim() || null,
        waste_pct: Number(waste),
        stories: Math.max(1, Number(stories) || 1),
        source: pdfFile ? "pdf" : "manual",
        pdf_filename: pdfFile?.name ?? null,
        pdfFile,
        values: Object.fromEntries(
          WALL_MEASUREMENT_FIELDS.map((f) => [f.key, num(draft[f.key] ?? "")]),
        ),
        roof:
          roof && saveRoof
            ? {
                name: name.trim() || null,
                average_pitch: roof.pitch,
                waste_pct: Number(roofWaste),
                layers: 1,
                source: "pdf",
                pdf_filename: pdfFile?.name ?? null,
                pdfFile,
                values: roof.values,
              }
            : null,
      });
    } finally {
      setBusy(false);
    }
  }

  const wasteOptions = [
    ...new Set([
      ...WASTE_PCT_OPTIONS,
      ...(waste !== "" && Number.isFinite(Number(waste))
        ? [Number(waste)]
        : []),
    ]),
  ].sort((a, b) => a - b);

  return (
    <Modal
      title={t("Measure the Walls")}
      subtitle={t(
        "{address} — upload the Hover report or enter the numbers by hand.",
        { address: job.address ?? t("This job") },
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Close")}
          </button>
          <button
            className="btn-primary"
            disabled={!ready}
            style={ready ? undefined : { opacity: 0.5, cursor: "not-allowed" }}
            onClick={() => void submit()}
          >
            {busy ? t("Saving…") : t("Submit")}
          </button>
        </>
      }
    >
      <div className="uploadrow">
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void readPdf(file);
            e.target.value = "";
          }}
        />
        <button
          className="btn-ghost"
          disabled={reading}
          onClick={() => fileInput.current?.click()}
        >
          {reading ? t("Reading…") : t("Upload Hover report")}
        </button>
        <span className="na-hint">
          {pdfFile
            ? `${pdfFile.name} — ${tn(
                filled.length,
                "1 measurement read. Check them before submitting.",
                "{n} measurements read. Check them before submitting.",
              )}`
            : t("Hover's Complete Measurements PDF. Optional.")}
        </span>
      </div>
      {readError && (
        <div className="err" style={{ margin: "8px 0" }}>
          {readError}
        </div>
      )}
      {nameWarning && (
        <div className="config-warn" role="alert" style={{ margin: "8px 0" }}>
          <strong>{t("Check this is the right report.")}</strong>{" "}
          {t("It is made out to")} <b>{nameWarning.reported}</b>,{" "}
          {t("and this job is")} <b>{nameWarning.customer}</b>.{" "}
          {t("Carry on if that is expected.")}
        </div>
      )}

      {roof && (
        <RoofOffer
          roof={roof}
          measured={layouts.length}
          save={saveRoof}
          onSave={setSaveRoof}
          onPitch={(pitch) => setRoof((r) => (r ? { ...r, pitch } : r))}
          waste={roofWaste}
          onWaste={setRoofWaste}
        />
      )}

      <div className="fgrid one">
        <div className="field">
          <label>{t("Name")}</label>
          <input
            placeholder={t("Name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label>
            {t("Waste pct")} <span className="req">*</span>
          </label>
          <select value={waste} onChange={(e) => setWaste(e.target.value)}>
            <option value="">{t("Waste pct")}</option>
            {wasteOptions.map((w) => (
              <option key={w} value={w}>
                {w}%
              </option>
            ))}
          </select>
          <span className="na-hint">
            {t("On the siding and trim. Hover’s table offers 0, 10 and 18.")}
          </span>
        </div>

        <div className="field">
          <label>{t("Stories")}</label>
          <select value={stories} onChange={(e) => setStories(e.target.value)}>
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {tn(n, "1 story", "{n} stories")}
              </option>
            ))}
          </select>
          <span className="na-hint">
            {t("One corner post per story, per corner.")}
          </span>
        </div>

        {WALL_MEASUREMENT_FIELDS.map((f) => (
          <div className="field" key={f.key}>
            <label>
              {t(f.label)}
              {filled.includes(f.key) && (
                <span className="na-hint" style={{ marginLeft: 6 }}>
                  {t("from PDF")}
                </span>
              )}
            </label>
            <input
              inputMode="decimal"
              placeholder={t(f.label)}
              aria-label={t(f.label)}
              value={draft[f.key] ?? ""}
              onChange={(e) =>
                setDraft((current) => ({
                  ...current,
                  [f.key]: e.target.value,
                }))
              }
            />
            {f.hint && (
              <span className="na-hint">
                {t("Hover: {hint}", { hint: f.hint })}
              </span>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
